import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'

import {
  getFlatsEmailRecipients,
  performFlatsMassAction,
} from '../../../../api'
import { useMassActions, useNotification } from '../../../../hooks'
import {
  getMassActionExcludeIdParam,
  getMassActionIdParam,
} from '../../../../utils/common'
import { stopPropagation } from '../../../../utils/events'
import { getIdFilterParam } from '../../../../utils/filter'
import { ConfirmationModal } from '../../../Confirmation'
import { useSendEmail } from '../../../Email'
import {
  FLAT_ACTION_ARCHIVE_ITEM,
  FLAT_ACTION_ARCHIVE_MASS,
  FLAT_ACTION_EMAIL_ITEM,
  FLAT_ACTION_MASS_EMAIL_ALL_USERS,
  FLAT_ACTION_MASS_EMAIL_LANDLORDS,
  FLAT_ACTION_MASS_EMAIL_TENANTS,
  FLAT_ACTION_RESTORE_ITEM,
  FLAT_ACTION_RESTORE_MASS,
  FLAT_TABLE_MASS_ACTION_NAMES,
} from '../../Flat.constants'
import {
  getFlatTableAsyncNotificationText,
  getFlatTableNotificationTitle,
} from '../../Flat.utils'

import {
  LANDLORDS_ROLE_API_PARAMS,
  TENANTS_ROLE_API_PARAMS,
} from './useFlatTableActions.constants'

export function useFlatTableActions({
  excludeIds,
  filterValues,
  pagination,
  selectAllPages,
  selectedIds,
  sub,
  unselectAllPages,
  onCleanSelection,
  onCloseEmailFormModal,
  onCloseModal,
  onReload,
  onSetEmailFormModal,
  onSetModal,
}) {
  const location = useLocation()

  const { t } = useTranslation()

  const [isPerformingMassAction, setPerformingMassAction] = useState(false)

  const { handleAddErrorNotification } = useNotification()

  const {
    getAsyncTaskId,
    handleAddAsyncMassActionNotification,
    handleAddFinishMassActionNotification,
  } = useMassActions({
    getAsyncNotificationText: getFlatTableAsyncNotificationText,
    getNotificationTitle: getFlatTableNotificationTitle,
  })

  function showConfirmModal(title, onOk, btnText) {
    onSetModal(
      <ConfirmationModal
        actionButtonsProps={{
          submitText: btnText,
        }}
        description={title}
        onCancel={onCloseModal}
        onSubmit={onOk}
      />
    )
  }

  function massAction(action, ids) {
    const id = getMassActionIdParam({ ids, selectAllPages, selectedIds })
    const exclude_id = getMassActionExcludeIdParam({
      excludeIds,
      selectAllPages,
    })

    const params = {
      action: FLAT_TABLE_MASS_ACTION_NAMES[action],
      all: selectAllPages,
      filters: {
        ...filterValues,
        exclude_id,
        id: getIdFilterParam({
          filterValues,
          ids: id,
          selectAllPages,
        }),
      },
    }

    const countOfItems = selectAllPages ? pagination.count : id.length

    onCloseModal()

    setPerformingMassAction(true)

    performFlatsMassAction(params)
      .then(({ data }) => {
        onCloseModal()
        onCleanSelection()

        const asyncTask = getAsyncTaskId(data)

        if (asyncTask) {
          handleAddAsyncMassActionNotification({
            action,
            pathname: location.pathname,
            sub,
            task: asyncTask,
            totalCount: countOfItems,
          })

          return
        }

        onReload()

        handleAddFinishMassActionNotification(data, {
          action,
          totalCount: countOfItems,
        })
      })
      .catch(() => {
        onCloseModal()
        handleAddErrorNotification('Notification.FlatMassActionErrorTitleKey')
      })
      .finally(() => {
        setPerformingMassAction(false)
      })
  }

  const { handleOpenCreationEmail } = useSendEmail({
    excludeIds,
    filterValues,
    getRecipients: getFlatsEmailRecipients,
    selectAllPages,
    selectedIds,
    onCloseEmailFormModal,
    onCloseModal,
    onSetEmailFormModal,
    onSetModal,
    onSuccessSending: unselectAllPages,
  })

  function setItemAction(action, item) {
    switch (action) {
      case FLAT_ACTION_EMAIL_ITEM:
        handleOpenCreationEmail({ additionalFilters: { id: [item.id] } })
        break
      case FLAT_ACTION_ARCHIVE_ITEM:
        showConfirmModal(
          t('Modal.FlatArchiveConfirmationTextKey'),
          e => {
            stopPropagation(e)
            massAction(action, item.id)
          },
          t('Modal.FlatArchiveBtnKey')
        )
        break
      case FLAT_ACTION_RESTORE_ITEM:
        showConfirmModal(
          t('Modal.FlatRestoreConfirmationTextKey'),
          e => {
            stopPropagation(e)
            massAction(action, item.id)
          },
          t('Modal.FlatRestoreBtnKey')
        )
        break
      default:
        return null
    }
  }

  function setMassAction(action) {
    switch (action) {
      case FLAT_ACTION_ARCHIVE_MASS:
        showConfirmModal(
          t('Modal.FlatMassArchiveConfirmationTextKey'),
          e => {
            stopPropagation(e)
            massAction(action)
          },
          t('Modal.FlatMassArchiveBtnKey')
        )
        break
      case FLAT_ACTION_RESTORE_MASS:
        showConfirmModal(
          t('Modal.FlatMassRestoreConfirmationTextKey'),
          e => {
            stopPropagation(e)
            massAction(action)
          },
          t('Modal.FlatMassRestoreBtnKey')
        )
        break
      case FLAT_ACTION_MASS_EMAIL_ALL_USERS:
        handleOpenCreationEmail()
        break
      case FLAT_ACTION_MASS_EMAIL_LANDLORDS:
        handleOpenCreationEmail(LANDLORDS_ROLE_API_PARAMS)
        break
      case FLAT_ACTION_MASS_EMAIL_TENANTS:
        handleOpenCreationEmail(TENANTS_ROLE_API_PARAMS)
        break
      default:
        return null
    }
  }

  return { isPerformingMassAction, setItemAction, setMassAction }
}
